import { useState, useEffect } from "react";
import {
  X,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  Mail,
  Phone,
  MessageCircle,
  Users,
} from "lucide-react";
import { showToast } from "@/components/ui/Toast";
import api from "@/utils/api";

interface NotificationContact {
  id: number;
  slate_id: number;
  name: string;
  designation: string | null;
  phone: string | null;
  email: string | null;
  notify_email: boolean;
  notify_whatsapp: boolean;
  is_active: boolean;
}

interface ContactFormData {
  name: string;
  designation: string;
  phone: string;
  email: string;
  notify_email: boolean;
  notify_whatsapp: boolean;
  is_active: boolean;
}

interface NotificationContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  schoolId: number;
  schoolName: string;
  onContactsChange?: (count: number) => void;
}

const initialContactForm: ContactFormData = {
  name: "",
  designation: "",
  phone: "",
  email: "",
  notify_email: true,
  notify_whatsapp: false,
  is_active: true,
};

const NotificationContactsModal = ({
  isOpen,
  onClose,
  schoolId,
  schoolName,
  onContactsChange,
}: NotificationContactsModalProps) => {
  const [contacts, setContacts] = useState<NotificationContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState<NotificationContact | null>(null);
  const [contactForm, setContactForm] = useState<ContactFormData>(initialContactForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  // Animation states
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle animation
  useEffect(() => {
    if (isOpen && !shouldRender) {
      setShouldRender(true);
      setTimeout(() => {
        setIsAnimating(true);
      }, 50);
    } else if (!isOpen && shouldRender) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setShowForm(false);
        setEditingContact(null);
        setContactForm(initialContactForm);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Fetch contacts when modal opens
  useEffect(() => {
    if (isOpen && schoolId) {
      fetchContacts();
    }
  }, [isOpen, schoolId]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await api.get<{ contacts: NotificationContact[] }>(
        `/schools/${schoolId}/notification-contacts`
      );
      const contactsList = response?.data?.contacts || [];
      setContacts(contactsList);
      const activeCount = contactsList.filter((c) => c.is_active).length;
      onContactsChange?.(activeCount);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      showToast("Failed to load contacts", "error");
    } finally {
      setLoading(false);
    }
  };

  const openContactForm = (contact?: NotificationContact) => {
    if (contact) {
      setEditingContact(contact);
      setContactForm({
        name: contact.name,
        designation: contact.designation || "",
        phone: contact.phone || "",
        email: contact.email || "",
        notify_email: contact.notify_email,
        notify_whatsapp: contact.notify_whatsapp,
        is_active: contact.is_active,
      });
    } else {
      setEditingContact(null);
      setContactForm(initialContactForm);
    }
    setShowForm(true);
  };

  const closeContactForm = () => {
    setShowForm(false);
    setEditingContact(null);
    setContactForm(initialContactForm);
  };

  const saveContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactForm.name.trim()) {
      showToast("Name is required", "error");
      return;
    }

    try {
      setSaving(true);
      const url = editingContact
        ? `/schools/${schoolId}/notification-contacts/${editingContact.id}`
        : `/schools/${schoolId}/notification-contacts`;
      const method = editingContact ? "put" : "post";

      const response = await api[method](url, contactForm);

      if (response?.data) {
        showToast(editingContact ? "Contact updated" : "Contact added", "success");
        closeContactForm();
        await fetchContacts();
      }
    } catch (error) {
      console.error("Error saving contact:", error);
      showToast("Failed to save contact", "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteContact = async (contactId: number) => {
    try {
      await api.delete(`/schools/${schoolId}/notification-contacts/${contactId}`);
      showToast("Contact deleted", "success");
      setDeleteConfirm(null);
      await fetchContacts();
    } catch (error) {
      console.error("Error deleting contact:", error);
      showToast("Failed to delete contact", "error");
    }
  };

  const toggleContactActive = async (contact: NotificationContact) => {
    try {
      await api.put(`/schools/${schoolId}/notification-contacts/${contact.id}`, {
        is_active: !contact.is_active,
      });
      await fetchContacts();
    } catch (error) {
      console.error("Error updating contact:", error);
      showToast("Failed to update contact", "error");
    }
  };

  if (!shouldRender) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/50 transition-opacity duration-300 cursor-pointer ${
          isAnimating ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Sidebar - positioned at extreme right */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl transition-transform duration-300 ease-out ${
          isAnimating ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand/10 rounded-lg flex items-center justify-center">
                <Users size={20} className="text-brand" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Notification Contacts</h2>
                <p className="text-sm text-gray-500">{schoolName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {!showForm ? (
              <div className="p-6">
                {/* Add Contact Button */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-gray-500">
                    {contacts.filter((c) => c.is_active).length} active contacts
                  </span>
                  <button
                    onClick={() => openContactForm()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-hover transition-colors"
                  >
                    <Plus size={16} />
                    Add Contact
                  </button>
                </div>

                {/* Contacts List */}
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 size={24} className="animate-spin text-brand" />
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Users size={24} className="text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No notification contacts yet</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Add contacts to receive deliverable notifications
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {contacts.map((contact) => (
                      <div
                        key={contact.id}
                        className={`p-4 border rounded-lg transition-colors ${
                          contact.is_active
                            ? "bg-white border-gray-200"
                            : "bg-gray-50 border-gray-200 opacity-60"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{contact.name}</span>
                              {!contact.is_active && (
                                <span className="text-xs px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded">
                                  Inactive
                                </span>
                              )}
                            </div>
                            {contact.designation && (
                              <p className="text-xs text-gray-500">{contact.designation}</p>
                            )}
                            <div className="mt-2 space-y-1">
                              {contact.phone && (
                                <div className="flex items-center text-xs text-gray-600">
                                  <Phone size={12} className="mr-1.5 text-gray-400" />
                                  {contact.phone}
                                </div>
                              )}
                              {contact.email && (
                                <div className="flex items-center text-xs text-gray-600">
                                  <Mail size={12} className="mr-1.5 text-gray-400" />
                                  {contact.email}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 mt-2">
                              {contact.notify_email && (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                  <Mail size={12} className="mr-1" />
                                  Email
                                </span>
                              )}
                              {contact.notify_whatsapp && (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                                  <MessageCircle size={12} className="mr-1" />
                                  WhatsApp
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => toggleContactActive(contact)}
                              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                              title={contact.is_active ? "Deactivate" : "Activate"}
                            >
                              {contact.is_active ? "🟢" : "⚪"}
                            </button>
                            <button
                              onClick={() => openContactForm(contact)}
                              className="p-1.5 text-gray-400 hover:text-brand hover:bg-gray-100 rounded"
                            >
                              <Edit2 size={16} />
                            </button>
                            {deleteConfirm === contact.id ? (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => deleteContact(contact.id)}
                                  className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                                >
                                  Yes
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm(null)}
                                  className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded"
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDeleteConfirm(contact.id)}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Contact Form */
              <div className="p-6">
                <div className="flex items-center gap-2 mb-6">
                  <button
                    type="button"
                    onClick={closeContactForm}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ← Back
                  </button>
                  <span className="text-sm font-medium text-gray-900">
                    {editingContact ? "Edit Contact" : "Add Contact"}
                  </span>
                </div>

                <form onSubmit={saveContact} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Designation
                    </label>
                    <input
                      type="text"
                      value={contactForm.designation}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, designation: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                      placeholder="e.g., Principal, Admin"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notification Preferences
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={contactForm.notify_email}
                          onChange={(e) =>
                            setContactForm({ ...contactForm, notify_email: e.target.checked })
                          }
                          className="mr-2 h-4 w-4 text-brand rounded"
                        />
                        <Mail size={16} className="mr-1 text-blue-500" />
                        <span className="text-sm">Email</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={contactForm.notify_whatsapp}
                          onChange={(e) =>
                            setContactForm({ ...contactForm, notify_whatsapp: e.target.checked })
                          }
                          className="mr-2 h-4 w-4 text-brand rounded"
                        />
                        <MessageCircle size={16} className="mr-1 text-green-500" />
                        <span className="text-sm">WhatsApp</span>
                      </label>
                    </div>
                  </div>

                  {editingContact && (
                    <div>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={contactForm.is_active}
                          onChange={(e) =>
                            setContactForm({ ...contactForm, is_active: e.target.checked })
                          }
                          className="mr-2 h-4 w-4 text-brand rounded"
                        />
                        <span className="text-sm">Active</span>
                      </label>
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeContactForm}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-hover disabled:opacity-50"
                    >
                      {saving && <Loader2 size={16} className="mr-2 animate-spin" />}
                      {editingContact ? "Update" : "Create"}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationContactsModal;
